import { A11yModule } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
    TemplateRef,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    ButtonComponent,
    IconButtonComponent,
    ImageComponent,
    KeyboardEventManagerService,
} from '@interacta-shared/ui';
import { uuid } from '@interacta-shared/util';
import { ThemeMode } from '@interacta-shared/util-common';
import { TranslatePipe } from '@ngx-translate/core';
import { aiThemedAssetSrc } from '../../utils/ai-assets';

@Component({
    selector: 'interacta-ai-discovery-dialog',
    templateUrl: './ai-discovery-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        A11yModule,
        ButtonComponent,
        IconButtonComponent,
        ImageComponent,
        NgTemplateOutlet,
        TranslatePipe,
    ],
})
export class AIDiscoveryDialogComponent {
    private readonly overlay = inject(Overlay);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly keyboardEventManager = inject(KeyboardEventManagerService);
    private readonly handlerId = uuid();

    readonly isOpen = input(false);
    readonly videoSrc = input.required<string>();
    readonly videoPosterSrc = input<string>();
    readonly themeMode = input<ThemeMode>('light');

    readonly openAIPanel = output<void>();
    readonly closing = output<void>();

    readonly titleId = uuid();

    readonly logoSrc = computed(() =>
        aiThemedAssetSrc('logo', this.themeMode()),
    );

    readonly features = [
        {
            title: 'AI.DISCOVERY.DIALOG_ANSWERS_TITLE',
            description: 'AI.DISCOVERY.DIALOG_ANSWERS_DESCRIPTION',
        },
        {
            title: 'AI.DISCOVERY.DIALOG_ATTACHMENTS_TITLE',
            description: 'AI.DISCOVERY.DIALOG_ATTACHMENTS_DESCRIPTION',
        },
        {
            title: 'AI.DISCOVERY.DIALOG_HISTORY_TITLE',
            description: 'AI.DISCOVERY.DIALOG_HISTORY_DESCRIPTION',
        },
    ];

    /*
     * An autoplaying video is motion the user did not ask for: under
     * prefers-reduced-motion the video is delivered paused, on its poster.
     */
    readonly autoplay = !(
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    private readonly dialogTemplate =
        viewChild.required<TemplateRef<unknown>>('dialogTemplate');

    private overlayRef: OverlayRef | null = null;
    private previouslyFocused: HTMLElement | null = null;

    constructor() {
        toObservable(this.isOpen)
            .pipe(takeUntilDestroyed())
            .subscribe((isOpen) => (isOpen ? this.attach() : this.detach()));

        inject(DestroyRef).onDestroy(() => this.detach());
    }

    private attach(): void {
        if (this.overlayRef) return;

        this.previouslyFocused = document.activeElement as HTMLElement | null;
        this.overlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: 'bg-dialog-layer',
            panelClass: ['interacta-dialog', 'h-fit'],
            positionStrategy: this.overlay
                .position()
                .global()
                .centerHorizontally()
                .centerVertically(),
        });
        this.overlayRef.attach(
            new TemplatePortal(this.dialogTemplate(), this.viewContainerRef),
        );

        this.keyboardEventManager.registerHandler({
            id: this.handlerId,
            scope: this.overlayRef.overlayElement,
            onEscape: () => this.closing.emit(),
        });
    }

    private detach(): void {
        if (!this.overlayRef) return;

        this.keyboardEventManager.deregisterHandler(this.handlerId);
        this.overlayRef.dispose();
        this.overlayRef = null;
        this.previouslyFocused?.focus();
        this.previouslyFocused = null;
    }
}
